import type { ReactNode } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { ForgotPassword, LoginVerify, SetPassword, VerifyPhone } from "@dearbill/shared";
import { AuthenticatedLayout } from "./shell/AuthenticatedLayout";
import { OrgList } from "./routes/OrgList";
import { OrgShell } from "./shell/OrgShell";
import { OrgIndexRedirect } from "./shell/OrgIndexRedirect";
import { RequireOrgAdmin } from "./shell/RequireOrgAdmin";
import { RequireOrgComplete } from "./shell/RequireOrgComplete";
import { OrganizationRoute } from "./routes/OrganizationRoute";
import { UserProfileRoute } from "./routes/UserProfileRoute";
import { MembersRoute } from "./routes/MembersRoute";
import { HomeTab } from "./routes/HomeTab";
import { EnrollmentsTab } from "./routes/EnrollmentsTab";
import { EnrollmentDetail } from "./routes/EnrollmentDetail";
import { CostToolTab } from "./routes/CostToolTab";
import { PharmacyOnboardingRoute } from "./routes/PharmacyOnboardingRoute";
import { PartnerLoginLanding } from "./routes/PartnerLoginLanding";

/**
 * Partner portal SPA. Auth is a self-issued partner-session bearer token, not
 * OIDC, held in localStorage by the shared `PortalAuthProvider` that `main.tsx`
 * mounts with `PARTNER_AUTH_CONFIG`. The unauthenticated screens are the shared
 * portal ones, running under that config.
 *
 * Every authenticated page nests under `AuthenticatedLayout`, which redirects to
 * `/login` without a session, provides the shared `GET /api/partner/me` record,
 * and renders the global top bar above the routed content. The authenticated
 * pages are the org list (`/`), the account profile (`/profile`, independent of
 * any org) and the org page (`/orgs/:orgId/*`).
 *
 * The org page is a tab-bar shell (`OrgShell`) with nested sections: Home
 * (`home`), Member cost share (`cost-tool`), Referrals (`forms`) with its read-only
 * referral detail, Team (`members`), and Organization (the org-profile form,
 * which also carries the Terms & Conditions box when the org type requires one).
 * Home, Member cost share and Referrals remain reachable while setup is pending.
 * `RequireOrgAdmin` guards Team at the route, not only by hiding its tab. The
 * pharmacy introduction (`get-started`) saves each member's place separately
 * from organization activation. Only a bare org entry can open it automatically.
 *
 * The unauthenticated routes sit outside the layout and never show the top bar.
 * `/verify-phone` is the first-time-setup SMS OTP step reached from
 * `/set-password`; `/verify-login` is the login 2FA OTP step reached from `/login`.
 */

/**
 * Redirects an unknown `/orgs/:orgId/*` path to the Organization tab.
 *
 * The target is absolute, built from the matched `orgId`. A relative
 * `<Navigate to="organization" replace />` from a splat (`path="*"`) match
 * appends onto the still-unmatched path on every render
 * (`.../foobar/organization/organization/…`), which loops until the tab crashes.
 */
function OrgCatchAllRedirect(): ReactNode {
  const { orgId } = useParams<{ orgId: string }>();
  return <Navigate to={orgId ? `/orgs/${orgId}/organization` : "/"} replace />;
}

export function App(): ReactNode {
  return (
    <Routes>
      <Route element={<AuthenticatedLayout />}>
        <Route path="/" element={<OrgList />} />
        <Route path="/profile" element={<UserProfileRoute />} />
        <Route path="/orgs/:orgId" element={<OrgShell />}>
          <Route index element={<OrgIndexRedirect />} />
          <Route path="get-started" element={<PharmacyOnboardingRoute />} />
          <Route
            path="home"
            element={
              <RequireOrgComplete>
                <HomeTab />
              </RequireOrgComplete>
            }
          />
          <Route path="organization" element={<OrganizationRoute />} />
          <Route
            path="members"
            element={
              <RequireOrgAdmin>
                <MembersRoute />
              </RequireOrgAdmin>
            }
          />
          <Route
            path="forms"
            element={
              <RequireOrgComplete>
                <EnrollmentsTab />
              </RequireOrgComplete>
            }
          />
          <Route
            path="forms/:enrollmentId"
            element={
              <RequireOrgComplete>
                <EnrollmentDetail />
              </RequireOrgComplete>
            }
          />
          {/* The splat lets CostToolTab route its two screens (the question
              form and `result`) under one stateful parent, so both share the
              calculator state. */}
          <Route
            path="cost-tool/*"
            element={
              <RequireOrgComplete>
                <CostToolTab />
              </RequireOrgComplete>
            }
          />
          <Route path="*" element={<OrgCatchAllRedirect />} />
        </Route>
      </Route>
      <Route path="/login" element={<PartnerLoginLanding />} />
      <Route path="/verify-login" element={<LoginVerify />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/verify-phone" element={<VerifyPhone />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
